// Registro de planos do lado do cliente — espelho de api-server/src/lib/plans.ts.
// Fica em lib/ (não em components/) porque o layout PÚBLICO do catálogo também
// pergunta "é plano pago?" e não deve puxar componente de admin pra isso.
export type PaidPlanId = "profissional" | "ultra";
export type BillingCycleUI = "monthly" | "yearly";

export interface PlanDef {
  id: PaidPlanId;
  name: string;
  monthlyCents: number;
  yearlyCents: number;
  features: readonly string[];
  /** Resumo do card do painel: 4 linhas curtas. A lista inteira vive em /assinar. */
  highlights?: readonly string[];
  /** Prometido na página de upgrade, ainda não entregue. */
  comingSoon?: readonly string[];
  allowsPrinter: boolean;
}

export const PLAN_DEFS: Record<PaidPlanId, PlanDef> = {
  profissional: {
    id: "profissional",
    name: "Profissional",
    monthlyCents: 4990,
    yearlyCents: 49900,
    // Pedro, 15/10/2026: o 1º item é «Tudo da versão grátis», como o Ultra tem «Tudo do Profissional»; os dois recursos
    // do Profissional com os mesmos títulos do editor.
    features: [
      "Tudo da versão grátis",
      "Pedidos ilimitados",
      "Impressão de pedidos",
      "Suporte pelo WhatsApp",
      "Adicionais fracionados: cada opção pode contar ½ ou 2 no limite do grupo",
      "Pacote de escolhas: limite total entre os grupos e extras pagos",
      "Mensagem no rodapé do cupom: troque o “Obrigado pela preferência!” pelo seu recado",
      "Taxa de entrega lançada como despesa no Financeiro",
    ],
    allowsPrinter: true,
  },
  ultra: {
    id: "ultra",
    name: "Ultra",
    monthlyCents: 19700,
    yearlyCents: 197000,
    features: [
      "Robô com IA no WhatsApp da loja: atende seus clientes, tira dúvidas, manda o link e avisa o status do pedido",
      "Tudo do Profissional",
      "Equipe com acesso por página",
      "Leitor de código de barras no PDV e no cadastro",
      "Cupons por dia, bairro e aniversário",
      "Aniversariantes do dia com lembrete e mensagem",
      "Crediário: venda a prazo com as parcelas controladas",
      "Caixa: abre, fecha e confere a gaveta do dia",
      "\"Mais vendidos\" em destaque no seu link",
      "Pontos e recompensas para o cliente voltar",
      "Pedido na mesa pelo QR Code, direto na comanda",
      "Custos cadastrados direto da nota fiscal",
    ],
    highlights: [
      "Robô com IA no WhatsApp da loja: atende seus clientes, tira dúvidas, manda o link e avisa o status do pedido",
      "Tudo do Profissional",
      "Equipe com acesso por página",
      "Crediário e Caixa",
    ],
    // Em breve (Pedro, 11/09/2026): aparece na página de upgrade com o selo
    // "Em breve" — promessa, não feature; some daqui quando entrar em `features`.
    comingSoon: [],
    allowsPrinter: true,
  },
};

export const PLAN_RANK: Record<string, number> = { gratis: 0, profissional: 1, ultra: 2 };

export function isPaidPlanId(plan: string | null | undefined): plan is PaidPlanId {
  return plan === "profissional" || plan === "ultra";
}

export function isUltra(plan: string | null | undefined): boolean {
  return plan === "ultra";
}

// Features que existem a partir do Profissional (espelho de PRO_FEATURES no servidor; 15/10/2026): prep_count = «Pacote
// de escolhas», option_weight = «Adicionais fracionados», receipt_footer = «Mensagem no rodapé do cupom» (23/10/2026),
// delivery_fee_expense = «Lançar a taxa de entrega como despesa» (23/10/2026), card_fee_expense = «Lançar a taxa da
// maquininha como despesa» (25/10/2026), product_video = «Vídeo no produto» (24/10/2026), service_fee_expense = «Lançar a
// taxa de serviço como despesa» (30/10/2026).
// As outras features pagas são do Ultra.
export const PRO_FEATURES = ["prep_count", "option_weight", "receipt_footer", "delivery_fee_expense", "card_fee_expense", "product_video", "service_fee_expense"] as const;

/** O plano mínimo da feature: "profissional" (PRO_FEATURES) ou "ultra". */
export function featurePlan(feature: string): PaidPlanId {
  return (PRO_FEATURES as readonly string[]).includes(feature) ? "profissional" : "ultra";
}

/** O plano da loja já dá a feature (sem contar as liberações). */
export function planHasFeature(plan: string | null | undefined, feature: string): boolean {
  return (PLAN_RANK[plan ?? ""] ?? 0) >= PLAN_RANK[featurePlan(feature)];
}

export function planAllowsPrinterUI(_plan: string): boolean {
  return true;
}

export function priceCents(plan: PaidPlanId, cycle: BillingCycleUI): number {
  return cycle === "yearly" ? PLAN_DEFS[plan].yearlyCents : PLAN_DEFS[plan].monthlyCents;
}

// Loja extra custa o MESMO que a primeira. Existiu um desconto de 80% pra
// unidade adicional que nunca chegou a ser cobrado: em 05/08/2026, as 218
// assinaturas ativas eram todas R$ 49,90 (mensal) ou R$ 499 (anual), nenhuma
// com valor reduzido, e no servidor a conta dos 80% não tinha nem chamador.
export function totalCents(plan: PaidPlanId, cycle: BillingCycleUI, units: number): number {
  return priceCents(plan, cycle) * Math.max(1, units);
}

export function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
